import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { resolveRuntimeScope } from "./apiBase";

export type SyncState = "loading" | "synced" | "local" | "error" | "collected";

/** 与 data.ts 共用同一个口径判定（apiBase.resolveRuntimeScope）。
 *  之前这里只看 URL 路径，而管理端 URL 固定是 /qunxing/admin/（不带分组），
 *  于是分组账号的读写全落到了 alliance 这个空 scope —— 军费/攻略明明有数据却显示为空。 */
function runtimeScope() {
  return resolveRuntimeScope();
}

function endpoint(key: string) {
  return `/wuying/api/portal/scopes/${runtimeScope()}/${encodeURIComponent(key)}`;
}

type PortalPayload<T> = {
  ok?: boolean;
  value: T | null;
  revision?: number;
  updated_at?: number | string | null;
};

export type SyncControls = {
  refresh: () => Promise<void>;
  flush: () => Promise<void>;
};

export type PortalUpload = {
  ok: boolean;
  name: string;
  kind: "image" | "file";
  url: string;
  file_id?: string;
};

/** cloud://<envId>.<customId>/<path> → https://<customId>.tcb.qcloud.la/<path> */
export function fileIdToHttps(fileId: string) {
  const matched = /^cloud:\/\/([^/]+)\/(.+)$/.exec(String(fileId || "").trim());
  if (!matched) return "";
  const host = matched[1].split(".")[1] || "";
  return host ? `https://${host}.tcb.qcloud.la/${matched[2]}` : "";
}

/** 大图先压一下：几 MB 的原图切片会很多、传得慢 */
async function compressImage(file: File, maxSide = 1600, quality = 0.82): Promise<Blob> {
  if (!/^image\//.test(file.type) || /gif|svg/.test(file.type)) return file;
  if (typeof createImageBitmap !== "function") return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
    if (scale >= 1 && file.size <= 300 * 1024) return file;
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
    return blob && blob.size < file.size ? blob : file;
  } catch {
    return file;
  }
}

/** 分片要转 base64（服务端按 base64 收）；分块拼字符串避免 apply 参数上限 */
function toBase64(bytes: Uint8Array): string {
  let out = "";
  const step = 0x8000;
  for (let i = 0; i < bytes.length; i += step) {
    out += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + step)) as number[]);
  }
  return btoa(out);
}

/**
 * 图片上传：**压缩后分片**上传。
 *
 * 为什么不整张直传：CloudBase 的 HTTP 入口有请求体上限（实测 64KB 过、128KB 报 413），
 * 一张真实截图必然超；把图转成 base64 塞进内容里同样会超。
 * 为什么不用 Web SDK 直传云存储：匿名登录能成功，但 uploadFile 报 `credentials not found`
 * （匿名会话拿不到存储临时凭证），所以改走服务端中转。
 *
 * 现在的做法：浏览器把图压到 1600px/JPEG，再切成 ~30KB 的片，
 * 逐片 POST 到 /wuying/api/uploads/chunk，最后 /finish 由服务端拼好写入云存储。
 * 内容里只存返回的链接 —— 几十字节，不会再撞上限。
 */
const CHUNK_BYTES = 30 * 1024;

export async function uploadPortalMedia(file: File, purpose: "activity" | "guide"): Promise<PortalUpload> {
  const blob = await compressImage(file);
  const bytes = new Uint8Array(await blob.arrayBuffer());
  const uploadId = `up${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  const parts: string[] = [];
  for (let i = 0; i < bytes.length; i += CHUNK_BYTES) {
    parts.push(toBase64(bytes.subarray(i, i + CHUNK_BYTES)));
  }
  if (!parts.length) throw new Error("图片是空的");

  for (let seq = 0; seq < parts.length; seq += 1) {
    const res = await fetch("/wuying/api/uploads/chunk", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ upload_id: uploadId, seq, data: parts[seq] })
    });
    if (!res.ok) {
      const info = await res.json().catch(() => ({})) as { error?: string };
      throw new Error(info.error || `第 ${seq + 1}/${parts.length} 片上传失败`);
    }
  }

  const res = await fetch("/wuying/api/uploads/finish", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ upload_id: uploadId, name: file.name, type: blob.type || file.type || "image/png", purpose })
  });
  const payload = await res.json().catch(() => ({})) as Partial<PortalUpload> & { error?: string };
  if (!res.ok || !payload.ok || !payload.url) {
    throw new Error(payload.error || "图片上传失败");
  }
  return { ok: true, name: file.name, kind: "image", url: String(payload.url), file_id: payload.file_id };
}

function fingerprint(value: unknown) {
  return JSON.stringify(value);
}

/** 保存结果广播：页面用它在「真的写进云端」或「被服务端拒绝」时给明确提示。
 *  之前保存失败只把 syncState 置成 error，用户看不到 —— 会误以为存上了。 */
export type PortalSaveEvent = { key: string; ok: boolean; error?: string; revision?: number };

function emitSave(detail: PortalSaveEvent) {
  try { window.dispatchEvent(new CustomEvent<PortalSaveEvent>("alliance-portal-save", { detail })); } catch { /* 隐私模式/无 window 时忽略 */ }
}

export function usePersistentState<T>(key: string, initialValue: T): [T, Dispatch<SetStateAction<T>>, SyncState, SyncControls] {
  const [value, setValue] = useState<T>(initialValue);
  const [syncState, setSyncState] = useState<SyncState>("loading");
  const hydrated = useRef(false);
  const valueRef = useRef(value);
  const serverFingerprint = useRef("");
  const serverRevision = useRef(0);
  const dirty = useRef(false);
  const saveTimer = useRef<number | null>(null);
  const saving = useRef<Promise<void> | null>(null);

  valueRef.current = value;

  const applyRemote = useCallback((nextValue: T) => {
    serverFingerprint.current = fingerprint(nextValue);
    dirty.current = false;
    setValue(nextValue);
    setSyncState("synced");
  }, []);

  const refresh = useCallback(async () => {
    if (dirty.current || saving.current) return;
    const query = serverRevision.current > 0 ? `?since=${encodeURIComponent(String(serverRevision.current))}` : "";
    const response = await fetch(`${endpoint(key)}${query}`, {
      credentials: "include",
      headers: { Accept: "application/json" },
      cache: "no-store"
    });
    if (!response.ok || !response.headers.get("content-type")?.includes("application/json")) throw new Error("API unavailable");
    const payload = await response.json() as PortalPayload<T> & { changed?: boolean };
    if (typeof payload.revision === "number") serverRevision.current = payload.revision;
    if (payload.changed === false) {
      setSyncState("synced");
      return;
    }
    const nextValue = payload.value === null ? initialValue : payload.value;
    if (fingerprint(nextValue) !== serverFingerprint.current) applyRemote(nextValue);
  }, [applyRemote, initialValue, key]);

  const save = useCallback(async () => {
    if (!hydrated.current) return;
    if (saveTimer.current !== null) {
      window.clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    if (saving.current) await saving.current;
    const snapshot = valueRef.current;
    const snapshotFingerprint = fingerprint(snapshot);
    if (snapshotFingerprint === serverFingerprint.current) {
      dirty.current = false;
      setSyncState("synced");
      return;
    }
    dirty.current = true;
    setSyncState("loading");
    const request = fetch(endpoint(key), {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ value: snapshot })
    }).then(async (response) => {
      const payload = await response.json() as PortalPayload<T> & { error?: string };
      if (!response.ok) throw new Error(payload.error || "Save failed");
      if (typeof payload.revision === "number") serverRevision.current = payload.revision;
      const accepted = payload.value === null || payload.value === undefined ? snapshot : payload.value;
      serverFingerprint.current = fingerprint(accepted);
      if (valueRef.current === snapshot || fingerprint(valueRef.current) === snapshotFingerprint) {
        applyRemote(accepted);
      } else {
        dirty.current = true;
      }
      emitSave({ key: key, ok: true, revision: serverRevision.current });
    }).catch((error) => {
      dirty.current = true;
      setSyncState("error");
      emitSave({ key: key, ok: false, error: error instanceof Error ? error.message : "保存失败" });
      throw error;
    }).finally(() => {
      saving.current = null;
    });
    saving.current = request;
    await request;
  }, [applyRemote, key]);

  useEffect(() => {
    let cancelled = false;
    window.localStorage.removeItem(`alliance:${runtimeScope()}:${key}`);
    fetch(endpoint(key), { credentials: "include", headers: { Accept: "application/json" }, cache: "no-store" })
      .then(async (response) => {
        if (!response.ok || !response.headers.get("content-type")?.includes("application/json")) throw new Error("API unavailable");
        return response.json() as Promise<PortalPayload<T> & { changed?: boolean }>;
      })
      .then((payload) => {
        if (cancelled) return;
        if (typeof payload.revision === "number") serverRevision.current = payload.revision;
        applyRemote(payload.value === null ? initialValue : payload.value);
      })
      .catch(() => {
        if (!cancelled) {
          serverFingerprint.current = fingerprint(initialValue);
          setSyncState("error");
        }
      })
      .finally(() => {
        hydrated.current = true;
      });
    return () => {
      cancelled = true;
      if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
    };
  }, [applyRemote, initialValue, key]);

  useEffect(() => {
    if (!hydrated.current) return;
    const currentFingerprint = fingerprint(value);
    if (currentFingerprint === serverFingerprint.current) {
      dirty.current = false;
      return;
    }
    dirty.current = true;
    setSyncState("local");
    if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => { void save().catch(() => undefined); }, 550);
    return () => {
      if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
    };
  }, [save, value]);

  useEffect(() => {
    const syncIfVisible = () => {
      if (document.visibilityState === "visible") void refresh().catch(() => setSyncState("error"));
    };
    const timer = window.setInterval(syncIfVisible, 10_000);
    window.addEventListener("focus", syncIfVisible);
    window.addEventListener("alliance-portal-revision", syncIfVisible);
    document.addEventListener("visibilitychange", syncIfVisible);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", syncIfVisible);
      window.removeEventListener("alliance-portal-revision", syncIfVisible);
      document.removeEventListener("visibilitychange", syncIfVisible);
    };
  }, [refresh]);

  const controls = useMemo(() => ({ refresh, flush: save }), [refresh, save]);
  return [value, setValue, syncState, controls];
}
